import { Dialog, DialogContent, DialogHeader, DialogTitle, Button, Input, Textarea, Label } from "@tellescope/ui";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useNewConversationDialog } from "./useNewConversationDialog";
import { CareTeamMemberMultiSelect } from "./CareTeamMemberMultiSelect";

interface NewConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConversationCreated: () => void;
  allowCareTeamSelection?: boolean;
}

export const NewConversationDialog = ({
  open,
  onOpenChange,
  onConversationCreated,
  allowCareTeamSelection,
}: NewConversationDialogProps) => {
  const { t, form, isPending, canSubmit, handleOpenChange, handleSubmit, careTeamUserIds, requiresCareTeamSelection } =
    useNewConversationDialog({ onOpenChange, onConversationCreated, allowCareTeamSelection });

  const careTeamProps = form.getCustomFieldProps("selectedCareTeamUserIds");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 md:hidden" onClick={() => handleOpenChange(false)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <DialogTitle className="text-base font-medium">{t("New Conversation")}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="p-4 space-y-4">
          {requiresCareTeamSelection && (
            <div className="space-y-2">
              <Label htmlFor="conversation-care-team">{t("Send to")}</Label>
              <CareTeamMemberMultiSelect
                id="conversation-care-team"
                careTeamUserIds={careTeamUserIds}
                value={careTeamProps.value}
                onChange={careTeamProps.onValueChange}
                disabled={isPending}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="conversation-subject">{t("Subject")}</Label>
            <Input
              id="conversation-subject"
              {...form.getFieldProps("subject")}
              placeholder={t("What is this conversation about?")}
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="conversation-message">{t("Message")}</Label>
            <Textarea
              id="conversation-message"
              {...form.getFieldProps("initialMessage")}
              placeholder={t("Write your message...")}
              className="min-h-[100px] resize-none"
              disabled={isPending}
            />
          </div>

          <Button className="w-full" onClick={handleSubmit} disabled={!canSubmit}>
            {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t("Start Conversation")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
